/**
 * useAvatarVoiceChat — realtime voice with an artist via the SAME ElevenLabs
 * Conversational AI agent the web app uses (HypeTunes_Web_2026
 * src/hooks/useAvatarVoiceSession.js). Faithful port — same endpoints, same
 * WebSocket protocol, same thread, same create_song tool — plus ONE addition
 * for the 3D world: the agent's streamed audio drives the character (talkBridge
 * amplitude + worldStore.residentTalking) so the resident animates while it
 * speaks. NO Whisper, NO new brain.
 *
 * Reliability fixes over a naive port:
 *  - PRE-CONNECT on engage (mount): the WebSocket opens as soon as the chat
 *    opens, so the first words aren't eaten by ~1s of connect latency.
 *  - wireMic() runs whenever BOTH the socket is open AND the mic is acquired,
 *    not only in ws.onopen — so press-then-connect and connect-then-press both work.
 *  - Mic + audio contexts are acquired/unlocked inside the button GESTURE
 *    (iOS blocks getUserMedia / suspends AudioContexts outside a user gesture).
 *  - Diagnostics: logs mic settings + outgoing RMS so "it can't hear me" is
 *    debuggable from the console.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';
import { useWorldStore } from '../state/worldStore';
import { talkBridge } from '../state/talkBridge';
import { audioEngine } from '../services/audioEngine';
import { serverNow, cityStay, onWorldConnect, citySocketId } from '../services/worldSocket';
import { answerToolCall, sendToolResult } from '../services/voiceTools';

const IDLE_TIMEOUT_MS = 90000; // auto-disconnect after silence so nothing bills forever

// ── THE SESSION ENDS IN CHARACTER, NOT MID-SENTENCE ─────────────────────────
//
// Khari, 2026-08-06: "if the user is speaking, the last message goes unheard in
// a quietly disconnected session. If we know the session is coming to a close,
// the avatar should say so.. 'yo, I think we're out of time, but I'm here if
// you need to speak more.'" And the rules: user speaking → farewell over them;
// avatar speaking → finish the statement, THEN farewell.
//
// The clock that matters is not ours to see: every agent is created with a
// 300-second provider-side cap (ElevenLabsAgentService, conversation.
// max_duration_seconds) and when IT lands the socket just closes — no event, no
// warning, indistinguishable from a crash. So OUR clock has to run shorter than
// theirs, with enough room left to say goodbye in.
//
// The farewell is asked for, not scripted: a user_message on the open socket
// tells the agent time is up, and it answers in its own voice and words — every
// artist says goodbye differently, because they actually do. The agent's own
// turn-taking gives Khari both rules for free: an instruction arriving while it
// speaks queues behind the current sentence rather than cutting it.
const MEMBER_CAP_S = 300;        // mirror of the agent config's own number
const FAREWELL_HEADROOM_S = 25;  // ask this early, so there is room to answer
const HARD_STOP_MARGIN_S = 3;    // and always end INSIDE the provider's kill
const FAREWELL_PROMPT = '(The session clock just ran out. Say a short, warm goodbye in your own voice — you have to go, but they can come back and talk to you any time. One or two sentences, then stop.)';
// OUT IN THE CITY the goodbye is for somewhere to be — and never cold (Avva
// Digital, 2026-10-23: "give a heads-up a few minutes early… and then leave them
// with something, maybe an invitation to walk with me").
const CITY_HEADS_UP = (what) => `(Just for you, not to read out: ${what || 'your next plan'} is in about three minutes. When it fits, let them know in your own words. If they are really struggling and need you, you can stay instead.)`;
const CITY_FAREWELL = (what, home) => `(It is time to go${what ? `: ${what}` : ''}. Say goodbye warmly in your own voice, and leave them with something${home ? '' : '; you can invite them to walk with you'}. One or two sentences, then stop.)`;
// The conversation's own length ran out, but they are not going anywhere yet:
// saying they had to go had an artist invite a visitor to walk with her and
// then stand still (Khari, 2026-10-23).
const CITY_TIME_UP = '(The time for this conversation is up. Say a short, warm goodbye in your own voice. You are not leaving yet, so do not say you have to go or invite them anywhere. One or two sentences, then stop.)';
const HEADS_UP_S = 180;
// The city holds an artist for ten minutes at a time (API CityLifeService
// HOLD_MAX_MS); an open conversation renews it well inside that.
const HOLD_RENEW_MS = 2 * 60000;
const TIME_UP_NOTICE = '(Time limit reached. Session closed.)';
// Mic energy (RMS) that counts as "the user is actually talking" while a reply
// is playing. Tuned high on purpose: a missed interruption costs a second of
// patience, a false one cuts the agent off mid-sentence for nothing.
const BARGE_IN_RMS = 0.055;
// ONE-WAY: the keep-alive's beat (inside the agent's 30 s re-engage timer), and
// how long a cue or a typed line holds it off (`user_activity` also keeps the
// agent from starting to speak for ~2 s, so it never runs while one is due).
const KEEPALIVE_MS = 10000;
const REPLY_HOLD_MS = 15000;

// `guest` + `email` route through the email-gated, no-auth guest voice endpoints
// (email-capped, ~2 min/session) instead of the logged-in voice-session.
//
// THE LIVING CITY (city/, 2026-10-23): `surface: 'city'` tells the server this
// visitor walked up to the artist out in the city, so their own session knows
// where they are, that it is public, and when they have to go. `mustGoAt`
// (server clock) brings the existing goodbye forward to that moment — the
// artist leaves for their next story in their own words. `onReply` hands each
// thing the artist says to the dock, which shares it with the people standing
// near them (bystanders hear only the artist's side). `engage` asks the city
// again as each conversation starts (they stop for you again, and say whether
// they have to go by now); `onEnded` lets them get on with their day the moment
// a conversation is over, instead of standing frozen until the dock is closed.
//
// ON SHIFT (2026-10-30, Khari: "Voice chat doesn't support jobs yet, so ordering
// is text only. — Please add it."): `job: { zone, roleKey, seat?: () => seat,
// onNote?: (line) => void }` starts the call AT A COUNTER. The request names the
// room and the job and NO avatar: the server answers with whoever is on shift
// (`data.avatarId`) and the tools the job granted (`data.job.tools`), which
// voiceTools sends to the one job executor. `onHeard` hands each thing the
// visitor SAID to the dock (as `onReply` does the artist's), and `say(text)`
// types into the open call, so a counter keeps one conversation whichever way
// you talk.
//
// ONE-WAY (2026-10-31, Khari: "the better fix seems like putting them into agent
// mode, and allowing one way audio. this way we avoid making recordings."):
// `oneWay: true` + `surface: 'venue'` + `venue` (the room's place slug) is a
// venue host talking to the one visitor they came down to (components/
// VenueAgent). `listen()` opens the call WITHOUT the mic (nothing is ever sent
// from the visitor's side but text); `say(text)` is what they type; `cue(text)`
// is a stage direction from the venue (API RoomDjDirector.cueAgent), sent the
// same way but never saved or shown as theirs. No goodbye beat, no idle hang-up
// (the venue ends it with the mingle), no duck (the set has its own mix), and
// the resident-talking flags are left alone (the performer moves by the stage's
// own clock). A `user_activity` keep-alive stops the agent re-engaging an empty
// silence (API ElevenLabsAgentService: turn_timeout 30, silence never ends it).
//
// IN THE AIR (2026-11-04, API sockets/heliCabin): `surface: 'heli'` + `heli`
// (the pad) is the one passenger aboard talking to the pilot while she flies; the
// server checks they are aboard by this World's socket (sent with it).
export default function useAvatarVoiceChat({ avatarId, token = AUTH_TOKEN, guest = false, email = '', surface = null, mustGoAt = null, nextActivity = null, onReply = null, engage = null, onEnded = null, job = null, onHeard = null, oneWay = false, venue = null, heli = null, learn = null }) {
  const [active, setActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [micOpen, setMicOpen] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState('');
  const [requestingMic, setRequestingMic] = useState(false); // browser permission prompt is up
  const [micDenied, setMicDenied] = useState(false);         // mic blocked → offer text fallback

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const playCtxRef = useRef(null);
  const micCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const procRef = useRef(null);
  const rafRef = useRef(null);
  const idleRef = useRef(null);
  const capRef = useRef(null);       // hard stop — always inside the provider's own kill
  const farewellRef = useRef(null);  // the earlier timer that asks for the goodbye
  const headsUpRef = useRef(null);   // out in the city: the heads-up a few minutes before
  const drainEndRef = useRef(null);  // grace timer between audio chunks of the goodbye
  const closingRef = useRef(null);   // null | 'asked' | 'done'
  const threadIdRef = useRef(null);
  // Which call and which spoken turn a tool ran in (the web hook's `turnRef`):
  // the knowledge-base tools need it.
  const turnRef = useRef({ call: '', said: 0 });
  const agentIdRef = useRef(null);
  const agentSpeakingRef = useRef(false);
  const micOpenRef = useRef(false);
  const interruptRef = useRef(null);   // stops the reply mid-chunk (barge-in)
  const avatarRef = useRef(avatarId);
  avatarRef.current = avatarId;
  const cityRef = useRef({ surface, mustGoAt, onReply, nextActivity, engage, onEnded, onHeard });
  cityRef.current = { surface, mustGoAt, onReply, nextActivity, engage, onEnded, onHeard };
  // The job the dock ASKED for (props, read at call time) and the one the server
  // CONFIRMED for this session — only the confirmed one routes tools, and the
  // artist who answered (server-resolved for a job) names the shared tools.
  const jobAskRef = useRef(job);
  jobAskRef.current = job;
  const sessionJobRef = useRef(null);
  const sessionAvatarRef = useRef(null);
  const typedRef = useRef('');        // the last line typed into the open call (say)
  const stayRef = useRef(null);
  const renewRef = useRef(null);      // out in the city: keeps the artist stopped while the call is open
  const renewOffRef = useRef(null);   // and the reconnect listener that does the same after a drop
  const oneWayRef = useRef(oneWay);
  oneWayRef.current = oneWay;
  const venueRef = useRef(venue);
  venueRef.current = venue;
  const heliRef = useRef(heli);
  heliRef.current = heli;
  // IN CLASS (2026-11-04, components/hypeu/CourseRunner): `surface: 'learn'` +
  // `learn: { course, lesson, page }` — the host's agent answers a typed question
  // out loud, briefed with the lesson (API LearnService.agentBrief); each saved
  // turn carries it too, so an answer counts on the course's live voice.
  const learnRef = useRef(learn);
  learnRef.current = learn;
  const keepAliveRef = useRef(null);  // one-way: the user_activity beat
  const replyDueRef = useRef(0);      // one-way: when a cue/typed line went in (the beat waits for its answer)
  const wakeOffRef = useRef(null);    // one-way: the gesture listener that resumes a suspended player (iOS)

  const setSpeakingBoth = useCallback((v) => {
    setSpeaking(v);
    if (v) setAwaitingReply(false);
    if (oneWayRef.current) { if (!v) replyDueRef.current = 0; return; }
    useWorldStore.getState().setResidentTalking(v);
    talkBridge.talking = v;
    if (!v) talkBridge.amplitude = 0;
  }, []);

  const endRef = useRef(() => {});
  const end = useCallback(() => {
    if (idleRef.current) { clearTimeout(idleRef.current); idleRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (procRef.current) { try { procRef.current.disconnect(); } catch { /* noop */ } procRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
    if (micCtxRef.current) { micCtxRef.current.close().catch(() => {}); micCtxRef.current = null; }
    if (playCtxRef.current) { playCtxRef.current.close().catch(() => {}); playCtxRef.current = null; }
    analyserRef.current = null;
    setActive(false); setConnecting(false); setAwaitingReply(false); setMicOpen(false);
    micOpenRef.current = false;
    setSpeakingBoth(false);
    agentSpeakingRef.current = false;
    interruptRef.current = null;
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
    if (wakeOffRef.current) { wakeOffRef.current(); wakeOffRef.current = null; }
    replyDueRef.current = 0;
    if (!oneWayRef.current) audioEngine.duck(false);
    if (capRef.current) { clearTimeout(capRef.current); capRef.current = null; }
    if (farewellRef.current) { clearTimeout(farewellRef.current); farewellRef.current = null; }
    if (headsUpRef.current) { clearTimeout(headsUpRef.current); headsUpRef.current = null; }
    if (drainEndRef.current) { clearTimeout(drainEndRef.current); drainEndRef.current = null; }
    if (renewRef.current) { clearInterval(renewRef.current); renewRef.current = null; }
    if (renewOffRef.current) { renewOffRef.current(); renewOffRef.current = null; }
    closingRef.current = null;
    if (agentIdRef.current) {
      fetch(`${API_BASE}${guest ? '/api/chat/guest/voice-session/end' : '/api/chat/voice-session/end'}`, {
        method: 'POST',
        headers: guest ? { 'Content-Type': 'application/json' } : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: agentIdRef.current }),
      }).catch((err) => console.warn('[VoiceSession] Agent cleanup failed:', err));
      agentIdRef.current = null;
    }
    threadIdRef.current = null;
    sessionJobRef.current = null;
    sessionAvatarRef.current = null;
  }, [token, guest, setSpeakingBoth]);
  endRef.current = end;

  const resetIdle = useCallback(() => {
    if (oneWayRef.current) return;   // the venue ends a one-way call, never silence
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => { endRef.current(); }, IDLE_TIMEOUT_MS);
  }, []);

  const saveTurn = useCallback((role, content) => {
    if (!threadIdRef.current) return;
    // AT A COUNTER, the job goes with it (2026-11-02): the API keeps the turn in
    // the artist's ordinary chat labelled as that shift ("working as the music
    // genius at Wax & Vinyl"), the way the typed counter's turns are.
    const jobAsk = jobAskRef.current;
    const job = sessionJobRef.current && jobAsk?.roleKey ? { zone: jobAsk.zone, roleKey: jobAsk.roleKey } : null;
    fetch(`${API_BASE}/api/chat/voice-session/save`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId: threadIdRef.current, messages: [{ role, content }], ...(job ? { job } : {}), ...(learnRef.current ? { learn: learnRef.current } : {}) }),
    }).catch((err) => console.warn('[VoiceSession] Save failed:', err));
  }, [token]);

  // Build the mic → WS pipe. Safe to call repeatedly / from either side; only
  // wires once BOTH the socket is open and the mic is acquired.
  const wireMic = useCallback(() => {
    if (procRef.current) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !streamRef.current || !micCtxRef.current) return;
    const micContext = micCtxRef.current;
    const source = micContext.createMediaStreamSource(streamRef.current);
    const processor = micContext.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);

      // BARGE-IN. The mic used to go deaf entirely while the agent spoke, so
      // the only way to stop it was to wait. Now speech gets through — but only
      // speech: everything the mic hears while a reply is playing is echo and
      // room tone until it clearly isn't, and forwarding that would have the
      // agent interrupting itself.
      //
      // Browser AEC (echoCancellation, on in prewarm) does the real work; this
      // energy gate is the backstop for what leaks past it. Deliberately high —
      // a missed interruption costs a second of patience, a false one cuts the
      // agent off mid-sentence for nothing.
      if (agentSpeakingRef.current) {
        let sum = 0;
        for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
        if (Math.sqrt(sum / input.length) < BARGE_IN_RMS) return;
        // Kill the reply LOCALLY and now. The server will also stop sending,
        // but audio already queued here would otherwise keep playing over the
        // interruption — which is exactly what makes an assistant feel deaf.
        interruptRef.current?.();
      }

      const int16 = new Int16Array(input.length);
      // Floor closed → send silence (not nothing) so turn detection still fires.
      if (micOpenRef.current) {
        for (let i = 0; i < input.length; i++) int16[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
      }
      const bytes = new Uint8Array(int16.buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      ws.send(JSON.stringify({ user_audio_chunk: btoa(binary) }));
    };
    source.connect(processor);
    processor.connect(micContext.destination);
    procRef.current = processor;
  }, []);

  // Acquire mic + unlock BOTH audio contexts — MUST run inside a user gesture (iOS).
  const prewarm = useCallback(async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!playCtxRef.current) playCtxRef.current = new AC({ sampleRate: 16000 });
    await playCtxRef.current.resume().catch(() => {});
    if (!streamRef.current) {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      micCtxRef.current = new AC({ sampleRate: 16000 });
    }
    await micCtxRef.current.resume().catch(() => {});
    wireMic();
  }, [wireMic]);

  const start = useCallback(async () => {
    if (active || connecting || wsRef.current) return;
    // Caption cleared too: the time-up notice is written THROUGH the caption and
    // deliberately outlives its session — a new session must not open under the
    // old one's closing line.
    setConnecting(true); setError(''); setCaption('');
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!playCtxRef.current) playCtxRef.current = new AC({ sampleRate: 16000 });
      await playCtxRef.current.resume().catch(() => {});
      // Out in the city: are they still free, and do they have to go by now?
      let goAt = Number(cityRef.current.mustGoAt) || 0;
      let cityWhat = cityRef.current.nextActivity || null;
      let goingHome = false;
      if (cityRef.current.engage) {
        const r = await cityRef.current.engage();
        if (r && !r.ok) { endRef.current(); setError(r.message || 'They have to go'); return; }
        if (r && !r.offline) { goAt = Number(r.mustGoAt) || 0; cityWhat = r.nextActivity || null; goingHome = !!r.nextAway; }
      }
      // AT A COUNTER the request names the room and the job, never the artist
      // (who is on shift is the server's answer). The seat is read now, so the
      // barista knows where the customer is as the call opens.
      const jobAsk = jobAskRef.current;
      let seatNow = null;
      try { seatNow = jobAsk?.seat ? jobAsk.seat() : null; } catch { /* no seat is fine */ }
      const res = await fetch(`${API_BASE}${guest ? '/api/chat/guest/voice-session' : '/api/chat/voice-session'}`, {
        method: 'POST',
        headers: guest ? { 'Content-Type': 'application/json' } : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(guest ? { email } : {}),
          ...(jobAsk?.roleKey
            ? { job: { zone: jobAsk.zone, roleKey: jobAsk.roleKey, ...(seatNow ? { seat: seatNow } : {}) } }
            : { avatarId: avatarRef.current }),
          ...(cityRef.current.surface ? { surface: cityRef.current.surface } : {}),
          ...(venueRef.current ? { venue: venueRef.current } : {}),
          ...(heliRef.current ? { heli: heliRef.current, worldSocketId: citySocketId() } : {}),
          ...(learnRef.current ? { learn: learnRef.current, ...(oneWayRef.current ? { oneWay: true } : {}) } : {}),
          // Held in the World: their move book comes with them (do_move, API city/moveBook).
          ...(guest ? {} : { world: true }),
        }),
      });
      const data = await res.json();
      if (res.status === 403 && data.requiresUpgrade) { setError(data.error || "That's your free voice time — sign up to keep talking."); setConnecting(false); return; }
      if (!data.success || !data.signedUrl) throw new Error(data.error || 'Failed to start voice session');
      threadIdRef.current = data.threadId || null;
      agentIdRef.current = data.agentId;
      sessionAvatarRef.current = data.avatarId || avatarRef.current || null;
      sessionJobRef.current = data.job && Array.isArray(data.job.tools) ? data.job : null;
      if (jobAsk?.roleKey && !sessionJobRef.current) console.warn('[VoiceSession] asked for a job, got an ordinary call:', data.jobRefused || 'no job in the answer');
      turnRef.current = { call: Math.random().toString(36).slice(2, 10), said: 0 };

      // The closing beat, on whichever clock binds. Guests get maxSeconds from
      // the server (~2 min); the member endpoint historically sent nothing, so
      // the fallback is the agent's own 300s — the cap that WAS ending member
      // calls, silently, by closing the socket. It never stopped applying; it
      // just finally gets a goodbye in front of it.
      let capSeconds = Number(data.maxSeconds) || MEMBER_CAP_S;
      // Out in the city the artist may have somewhere to be: when that comes
      // before the conversation's own limit, the goodbye is for THAT (a heads-up
      // first, then they go). Otherwise it is only the conversation ending.
      let leaving = false;
      // ⚠ The goodbye is asked for BEFORE they have to go, not at it: the city's
      // hold ends at `mustGoAt`, and with the goodbye asked right then the body
      // was already walking off while it spoke (2026-10-24).
      if (goAt) {
        const left = (goAt - serverNow()) / 1000;
        if (Number.isFinite(left) && left <= capSeconds) {
          leaving = true;
          capSeconds = Math.max(FAREWELL_HEADROOM_S + 6, left);
        }
      }
      const inCity = cityRef.current.surface === 'city';
      closingRef.current = null;

      const endForTime = () => {
        if (closingRef.current === 'done') return;
        closingRef.current = 'done';
        // The caption outlives the session on every dock, so the farewell is
        // legible as a rule rather than a brush-off — the other half of the
        // spec, and neither half works alone.
        setCaption(TIME_UP_NOTICE);
        endRef.current();
      };
      const sayGoodbye = () => {
        if (closingRef.current) return;
        closingRef.current = 'asked';
        const sock = wsRef.current;
        if (!sock || sock.readyState !== WebSocket.OPEN) { endForTime(); return; }
        try {
          sock.send(JSON.stringify({ type: 'user_message', text: leaving ? CITY_FAREWELL(cityWhat, goingHome) : inCity ? CITY_TIME_UP : FAREWELL_PROMPT }));
        } catch { endForTime(); }
      };
      // The timers, (re)set from a number of seconds left — again when the
      // artist chooses to stay and the schedule yields (stay_with_them).
      const arm = (secs) => {
        // One-way: no goodbye beat (the venue's own moments carry that); only
        // the stop inside the provider's kill, quietly (VenueAgent opens a new one).
        if (oneWayRef.current) {
          if (capRef.current) clearTimeout(capRef.current);
          capRef.current = setTimeout(() => endRef.current(), Math.max(8, secs - HARD_STOP_MARGIN_S) * 1000);
          return;
        }
        if (farewellRef.current) clearTimeout(farewellRef.current);
        farewellRef.current = setTimeout(sayGoodbye, Math.max(5, secs - FAREWELL_HEADROOM_S) * 1000);
        if (capRef.current) clearTimeout(capRef.current);
        capRef.current = setTimeout(endForTime, Math.max(8, secs - HARD_STOP_MARGIN_S) * 1000);
        if (headsUpRef.current) { clearTimeout(headsUpRef.current); headsUpRef.current = null; }
        if (leaving && secs > HEADS_UP_S + 20) {
          headsUpRef.current = setTimeout(() => {
            const sock = wsRef.current;
            if (sock?.readyState === WebSocket.OPEN) { try { sock.send(JSON.stringify({ type: 'contextual_update', text: CITY_HEADS_UP(cityWhat) })); } catch { /* the goodbye still comes */ } }
          }, (secs - HEADS_UP_S) * 1000);
        }
      };
      let stayed = false;
      stayRef.current = (untilMs, goes) => {
        closingRef.current = null;
        leaving = !!goes;
        stayed = true;
        arm(Math.max(60, (untilMs - serverNow()) / 1000));
      };
      arm(capSeconds);

      /* ⚠ THE STOP LASTS AS LONG AS THE CONVERSATION (Khari, 2026-10-24: "while
         talking to him, he walked away without telling me he had to leave").
         The city held an artist for ten minutes and only `engage` renewed it,
         once, as the call started; a longer call (an admin's runs up to an hour)
         outlived the hold, and the body walked off mid-sentence while the voice
         kept talking. So an open call renews it, and asks again the moment the
         world socket reconnects (the hold is keyed to the socket and dropped
         with it). A renewal that finds they now have somewhere to be (a plan
         that came up during the call) arms the goodbye for it; one the city
         refuses (they are due somewhere now) asks for the goodbye at once.
         After a stay the interval stands down: stay_with_them set a longer hold
         that a renewal would shorten, and its own goodbye is armed. */
      if (inCity && cityRef.current.engage) {
        const renew = async ({ afterDrop = false } = {}) => {
          if (closingRef.current || !wsRef.current || (stayed && !afterDrop)) return;
          const r = await cityRef.current.engage().catch(() => null);
          if (!r || r.offline || closingRef.current || !wsRef.current) return;
          if (!r.ok) { leaving = true; sayGoodbye(); return; }
          const goes = Number(r.mustGoAt) || 0;
          if (goes && (!leaving || Math.abs(goes - goAt) > 5000)) {
            leaving = true; goAt = goes;
            cityWhat = r.nextActivity || cityWhat; goingHome = !!r.nextAway;
            arm(Math.max(FAREWELL_HEADROOM_S + 6, (goes - serverNow()) / 1000));
          }
        };
        renewRef.current = setInterval(() => { renew(); }, HOLD_RENEW_MS);
        renewOffRef.current = onWorldConnect(() => { renew({ afterDrop: true }); });
      }
      // The backstop the farewell cannot escape: a mute agent, a dead socket, a
      // goodbye that will not stop talking — whatever happens, WE end it, inside
      // the provider's own kill, so the last thing seen is our notice and never
      // an unexplained drop.
      // (The backstop — the farewell cannot escape it — is armed by arm() above.)

      const audioContext = playCtxRef.current;
      // One-way calls open without a tap (the mingle starts them): a player the
      // browser left suspended resumes on the next touch or key, and anything
      // the agent said meanwhile plays from there.
      if (oneWayRef.current && audioContext.state !== 'running' && !wakeOffRef.current) {
        const wake = () => { audioContext.resume().catch(() => {}); };
        const evs = ['pointerdown', 'touchend', 'keydown'];
        evs.forEach((e) => window.addEventListener(e, wake, { passive: true }));
        wakeOffRef.current = () => evs.forEach((e) => window.removeEventListener(e, wake));
      }
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.connect(audioContext.destination);
      analyserRef.current = analyser;
      const ampData = new Uint8Array(analyser.frequencyBinCount);
      const ampTick = () => {
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(ampData);
        let sum = 0;
        for (let i = 0; i < ampData.length; i++) { const v = (ampData[i] - 128) / 128; sum += v * v; }
        talkBridge.amplitude = agentSpeakingRef.current ? Math.sqrt(sum / ampData.length) : talkBridge.amplitude * 0.8;
        rafRef.current = requestAnimationFrame(ampTick);
      };
      ampTick();

      const ws = new WebSocket(data.signedUrl);
      wsRef.current = ws;

      const queue = [];
      let playing = false;
      let curSrc = null;
      const playNext = () => {
        if (queue.length === 0) {
          playing = false; curSrc = null; agentSpeakingRef.current = false; setSpeakingBoth(false);
          // The goodbye has (probably) finished. Audio arrives in chunks, so an
          // empty queue can also be a gap mid-sentence — the grace window lets a
          // late chunk cancel the ending (see the audio branch below). Barging
          // in over the farewell lands here too, via the interrupt clearing the
          // queue: talking over the goodbye ends the call, which is the rule.
          if (closingRef.current === 'asked') {
            if (drainEndRef.current) clearTimeout(drainEndRef.current);
            drainEndRef.current = setTimeout(endForTime, 1200);
          }
          return;
        }
        playing = true; agentSpeakingRef.current = true; setSpeakingBoth(true);
        const pcm = queue.shift();
        const float32 = new Float32Array(pcm.length);
        for (let i = 0; i < pcm.length; i++) float32[i] = pcm[i] / 32768;
        const buffer = audioContext.createBuffer(1, float32.length, 16000);
        buffer.getChannelData(0).set(float32);
        const src = audioContext.createBufferSource();
        src.buffer = buffer;
        src.connect(analyser);
        src.onended = playNext;
        curSrc = src;
        src.start();
      };

      // Stop talking, immediately. Audio arrives in chunks and buffers ahead, so
      // dropping the queue is not enough on its own — the chunk already playing
      // has to be cut too, or the reply carries on for a beat after you have
      // started speaking. Clearing onended first stops it pulling the next one.
      interruptRef.current = () => {
        queue.length = 0;
        if (curSrc) {
          try { curSrc.onended = null; curSrc.stop(); } catch { /* already ended */ }
          curSrc = null;
        }
        playing = false;
        agentSpeakingRef.current = false;
        setSpeakingBoth(false);
      };

      ws.onopen = () => {
        setActive(true); setConnecting(false); resetIdle();
        // STANDARD DUCK, NOT SILENCE (Khari, 2026-08-04: "duck the music when I
        // start a voice conversation with any avatar" … "not muted.. same level
        // of standard ducking"). The bed stays audible under the conversation —
        // the city keeps playing while you talk to somebody in it, which is the
        // whole feeling. `full` is reserved for video calls, where the music is
        // genuinely not part of the moment.
        if (oneWayRef.current) {
          // One-way: the set has its own mix (VenueStage), and nobody speaks
          // here, so the keep-alive is what tells the agent not to fill the quiet.
          keepAliveRef.current = setInterval(() => {
            if (ws.readyState !== WebSocket.OPEN || agentSpeakingRef.current) return;
            if (replyDueRef.current && Date.now() - replyDueRef.current < REPLY_HOLD_MS) return;
            try { ws.send(JSON.stringify({ type: 'user_activity' })); } catch { /* closing */ }
          }, KEEPALIVE_MS);
          return;
        }
        audioEngine.duck(true);
        wireMic(); // wires if the mic is already acquired; else prewarm() will
      };

      ws.onmessage = (event) => {
        resetIdle();
        try {
          const msg = JSON.parse(event.data);
          // ANSWER THE KEEP-ALIVE: ElevenLabs sends `ping` and expects `pong`
          // with its event id (the web hook has answered since 2026-10-12, when
          // unanswered pings were part of calls dropping mid-statement).
          if (msg.type === 'ping') {
            const eventId = msg.ping_event?.event_id;
            if (eventId != null && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'pong', event_id: eventId })); } catch { /* closing */ }
            }
            return;
          }
          const audioB64 = msg.audio || msg.audio_event?.audio_base_64;
          if (audioB64) {
            // Still talking — a drained queue was a chunk gap, not the end of
            // the goodbye. Stand the ending down until it drains again.
            if (drainEndRef.current) { clearTimeout(drainEndRef.current); drainEndRef.current = null; }
            const bin = atob(audioB64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            queue.push(new Int16Array(bytes.buffer));
            if (!playing) playNext();
          }
          // The agent tells us when it has been cut off; honour it locally so
          // buffered audio does not outlive the interruption.
          if (msg.type === 'interruption' || msg.interruption_event) interruptRef.current?.();
          const tEvent = msg.user_transcription_event || msg.user_transcript_event;
          if (msg.type === 'user_transcript' && tEvent?.user_transcript) {
            // A line typed through say() is already saved and shown: if the
            // provider echoes it back as a transcript, it is not said twice.
            if (typedRef.current && tEvent.user_transcript.trim() === typedRef.current) { typedRef.current = ''; return; }
            turnRef.current.said += 1;
            saveTurn('user', tEvent.user_transcript);
            try { cityRef.current.onHeard?.(tEvent.user_transcript); } catch { /* the dock's copy is best effort */ }
          }
          if (msg.type === 'agent_response' && msg.agent_response_event?.agent_response) {
            const text = msg.agent_response_event.agent_response;
            setCaption(text);
            saveTurn('assistant', text);
            try { cityRef.current.onReply?.(text); } catch { /* sharing is best effort */ }
          }
          if (msg.type === 'client_tool_call' && msg.client_tool_call) {
            const call = msg.client_tool_call;
            // THE SCHEDULE YIELDS: the artist chose to stay with this person
            // (fire and forget on the agent's side; the city does the yielding).
            if (call.tool_name === 'stay_with_them') {
              if (cityRef.current.surface === 'city') {
                cityStay(avatarRef.current).then((r) => { if (r?.ok && r.until) stayRef.current?.(r.until, r.goes); }).catch(() => {});
              }
              return;
            }
            // A MOVE THEY CHOSE TO DO (API city/moveBook): relayed to the server, which
            // plays it for everyone here (socket city_emote) and says how it went.
            if (call.tool_name === 'do_move') {
              const ws0 = wsRef.current;
              if (guest || !token) { sendToolResult(ws0, call, 'Not here, not right now. Carry on, without mentioning tools.'); return; }
              fetch(`${API_BASE}/api/chat/voice-tool`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ avatarId: sessionAvatarRef.current || avatarRef.current, tool: 'do_move', parameters: call.parameters || {}, world: true, worldSocketId: citySocketId() }),
              }).then((r) => r.json()).then((d) => sendToolResult(wsRef.current, call, d?.result || 'Carry on.'))
                .catch(() => sendToolResult(wsRef.current, call, 'That did not come off just now. Carry on, without mentioning tools.'));
              return;
            }
            // EVERY OTHER TOOL GETS AN ANSWER (services/voiceTools): the same
            // tools as the web app, the same server bodies. Before 2026-10-29
            // only create_song was answered here, and any other tool the artist
            // reached for stalled the call.
            // On shift, the job's tools go to the job executor (voiceTools);
            // the seat and the note are the dock's, read at call time.
            const sj = sessionJobRef.current;
            answerToolCall(call, {
              ws: () => wsRef.current, token, guest, avatarId: sessionAvatarRef.current || avatarRef.current,
              threadId: threadIdRef.current, turn: `${turnRef.current.call}:${turnRef.current.said}`,
              job: sj ? { ...sj, seat: jobAskRef.current?.seat || null, onNote: jobAskRef.current?.onNote || null } : null,
            });
          }
        } catch { /* binary/unparseable — ignore */ }
      };

      ws.onerror = (err) => { console.error('[VoiceSession] WS error:', err); setError('Voice session error'); endRef.current(); };
      ws.onclose = () => { setActive(false); setSpeakingBoth(false); };
    } catch (err) {
      console.error('[VoiceSession] Failed to start:', err);
      setError(err.message || 'Failed to start voice session');
      setConnecting(false);
    }
  }, [active, connecting, token, guest, email, resetIdle, saveTurn, setSpeakingBoth, wireMic]);
  const startRef = useRef(start);
  startRef.current = start;

  // A conversation that was open and now is not, however it ended.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (active) { wasActiveRef.current = true; return; }
    if (!wasActiveRef.current) return;
    wasActiveRef.current = false;
    try { cityRef.current.onEnded?.(); } catch { /* the dock's close still releases */ }
  }, [active]);

  // TALK IS A TOGGLE, NOT A HOLD.
  //
  // Hold-to-talk asked people to learn a gesture before they could say anything,
  // and it reads as broken the first time you tap and nothing happens. It also
  // fights the medium: this is a STREAMING agent that handles its own
  // turn-taking, so gating the mic on a held button was throwing away the one
  // thing it is good at.
  //
  // Press once: the music ducks and the conversation is open until you end it.
  // The mic stays live throughout — interrupting is just talking, the way it is
  // on a phone call.
  const toggleTalk = useCallback(async () => {
    // Second press = hang up. Reads as one control with two states rather than
    // a start button and a separate, easily-missed end button.
    if (active || connecting || micOpenRef.current) { endRef.current(); return; }

    micOpenRef.current = true; setMicOpen(true); setError(''); setRequestingMic(true);
    try {
      // Mic + audio unlock must happen INSIDE the gesture or the browser
      // refuses both — that constraint is why this was a hold in the first
      // place, and a click satisfies it just as well.
      await prewarm();
    } catch (err) {
      console.error('[VoiceSession] mic prewarm failed:', err);
      micOpenRef.current = false; setMicOpen(false); setRequestingMic(false); setMicDenied(true);
      setError('Mic access is blocked');
      return;
    }
    setRequestingMic(false);
    // Connect ONLY now, on a deliberate press, so entering the world — or just
    // seeing the mic — never creates or bills a session. start() ducks the
    // music (duck(true)); end() restores it.
    if (!active && !connecting && !wsRef.current) startRef.current();
    else wireMic();
  }, [active, connecting, prewarm, wireMic]);

  // TYPING INTO AN OPEN CALL (2026-10-30). At a counter the text box stays
  // live during a call (never disabled — the platform rule), and a typed line
  // goes to the SAME agent as a user_message rather than to a second brain, so
  // the barista answers it out loud in the one conversation. False when no call
  // is open — the dock then sends it by text as usual.
  const say = useCallback((text) => {
    const t = String(text || '').trim();
    const sock = wsRef.current;
    if (!t || !sock || sock.readyState !== WebSocket.OPEN) return false;
    try {
      sock.send(JSON.stringify({ type: 'user_message', text: t }));
      typedRef.current = t;
      turnRef.current.said += 1;
      replyDueRef.current = Date.now();
      saveTurn('user', t);
      resetIdle();
      return true;
    } catch { return false; }
  }, [saveTurn, resetIdle]);

  // ONE-WAY: a stage direction from the venue (API RoomDjDirector.cueAgent).
  // Sent like a typed line, so the agent answers it out loud, but it is not the
  // visitor's words: never saved to the thread, never counted as a turn.
  const cue = useCallback((text) => {
    const t = String(text || '').trim();
    const sock = wsRef.current;
    if (!t || !sock || sock.readyState !== WebSocket.OPEN) return false;
    try {
      sock.send(JSON.stringify({ type: 'user_message', text: t }));
      typedRef.current = t;
      replyDueRef.current = Date.now();
      return true;
    } catch { return false; }
  }, []);

  // Clean up on unmount only. NEVER auto-connect on mount — a session is created
  // strictly on the first deliberate press (above).
  useEffect(() => () => { endRef.current(); }, []);

  const phase = useMemo(() => {
    if (error) return 'error';
    if (connecting && !active) return 'connecting';
    if (speaking) return 'speaking';
    if (micOpen && active) return 'listening';
    if (awaitingReply) return 'thinking';
    return 'idle';
  }, [error, connecting, speaking, micOpen, active, awaitingReply]);

  // ONE-WAY: open the call with no mic at all (start() never asks for one; only
  // prewarm does, and toggleTalk is the only caller of that).
  const listen = useCallback(() => startRef.current(), []);

  return { phase, caption, error, active, connecting, speaking, toggleTalk, end, requestingMic, micDenied, say, cue, listen };
}
